import { useState, useEffect } from 'react';
import { reportAPI } from '../../lib/api';
import { 
  FileText, 
  FilePdf,
  FileCsv,
  DownloadSimple,
  Shield,
  CurrencyDollar,
  Calendar,
  ChartBar,
  Funnel,
  Spinner
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function AdminReportsPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      setLoading(true);
      const response = await reportAPI.getAvailable();
      setReports(response.data.reports || []);
    } catch (error) {
      console.error('Error loading reports:', error);
      toast.error('Failed to load available reports');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async (reportId, format) => {
    setDownloading(`${reportId}-${format}`);
    try {
      let response;
      const filename = `${reportId}_${new Date().toISOString().split('T')[0]}.${format}`;
      
      switch (reportId) {
        case 'consent-compliance':
          response = await reportAPI.downloadConsentCompliance(format);
          break;
        case 'billing-summary':
          response = await reportAPI.downloadBillingSummary(format, dateRange.startDate, dateRange.endDate);
          break;
        case 'appointments':
          response = await reportAPI.downloadAppointments(format, dateRange.startDate, dateRange.endDate);
          break;
        case 'admin-summary':
          response = await reportAPI.downloadAdminSummary(format);
          break;
        default:
          throw new Error('Unknown report type');
      }
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: format === 'pdf' ? 'application/pdf' : 'text/csv' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Report downloaded: ${filename}`);
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error('Failed to download report');
    } finally {
      setDownloading(null);
    }
  };

  const getReportIcon = (reportId) => {
    switch (reportId) {
      case 'consent-compliance':
        return Shield;
      case 'billing-summary':
        return CurrencyDollar;
      case 'appointments':
        return Calendar;
      case 'admin-summary':
        return ChartBar;
      default:
        return FileText;
    }
  };

  const getReportColor = (reportId) => {
    switch (reportId) {
      case 'consent-compliance':
        return 'bg-teal-100 text-teal-600';
      case 'billing-summary':
        return 'bg-green-100 text-green-600';
      case 'appointments':
        return 'bg-blue-100 text-blue-600';
      case 'admin-summary':
        return 'bg-purple-100 text-purple-600';
      default:
        return 'bg-slate-100 text-slate-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reports</h1>
          <p className="text-slate-500">Download compliance, billing, and appointment reports</p>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="card-clinical p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-center gap-2">
            <Funnel size={18} className="text-slate-500" />
            <span className="font-medium text-slate-700">Date Range (for billing & appointment reports):</span>
          </div>
          <div className="flex items-center gap-3">
            <div>
              <label className="text-sm text-slate-500">From</label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                className="block mt-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="text-sm text-slate-500">To</label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                className="block mt-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Reports Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {reports.map((report) => {
          const Icon = getReportIcon(report.id);
          const colorClass = getReportColor(report.id);
          
          return (
            <div key={report.id} className="card-clinical overflow-hidden" data-testid={`report-${report.id}`}>
              <div className="p-6">
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-xl ${colorClass}`}>
                    <Icon size={28} weight="duotone" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-slate-900">{report.name}</h3>
                    <p className="text-slate-500 text-sm mt-1">{report.description}</p>
                  </div>
                </div>
                
                <div className="mt-6 pt-4 border-t border-slate-100">
                  <p className="text-sm text-slate-500 mb-3">Download as:</p>
                  <div className="flex gap-3">
                    {report.formats.includes('pdf') && (
                      <button
                        onClick={() => downloadReport(report.id, 'pdf')}
                        disabled={downloading === `${report.id}-pdf`}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-50 hover:bg-red-100 text-red-700 rounded-xl transition-colors disabled:opacity-50"
                      >
                        {downloading === `${report.id}-pdf` ? (
                          <div className="spinner w-4 h-4 border-red-600" />
                        ) : (
                          <FilePdf size={20} weight="duotone" />
                        )}
                        <span className="font-medium">PDF</span>
                      </button>
                    )}
                    {report.formats.includes('csv') && (
                      <button
                        onClick={() => downloadReport(report.id, 'csv')}
                        disabled={downloading === `${report.id}-csv`}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-50 hover:bg-green-100 text-green-700 rounded-xl transition-colors disabled:opacity-50"
                      >
                        {downloading === `${report.id}-csv` ? (
                          <div className="spinner w-4 h-4 border-green-600" />
                        ) : (
                          <FileCsv size={20} weight="duotone" />
                        )}
                        <span className="font-medium">CSV</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="card-clinical p-6">
        <h3 className="font-semibold text-slate-900 mb-4">Quick Export</h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => downloadReport('admin-summary', 'pdf')}
            disabled={downloading !== null}
            className="px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
          >
            <DownloadSimple size={18} />
            Admin Summary (PDF)
          </button>
          <button
            onClick={() => downloadReport('consent-compliance', 'csv')}
            disabled={downloading !== null}
            className="px-4 py-2 bg-teal-100 hover:bg-teal-200 text-teal-700 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
          >
            <DownloadSimple size={18} />
            Consent Data (CSV)
          </button>
          <button
            onClick={() => downloadReport('appointments', 'csv')}
            disabled={downloading !== null}
            className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
          >
            <DownloadSimple size={18} />
            Appointments (CSV)
          </button>
          <button
            onClick={() => downloadReport('billing-summary', 'pdf')}
            disabled={downloading !== null}
            className="px-4 py-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
          >
            <DownloadSimple size={18} />
            Billing Report (PDF)
          </button>
        </div>
      </div>

      {/* Info Note */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <FileText size={20} className="text-slate-500 mt-0.5" />
          <div>
            <p className="font-medium text-slate-700">About Reports</p>
            <p className="text-sm text-slate-500 mt-1">
              PDF reports include formatted headers, summaries, and professional styling suitable for printing or sharing.
              CSV exports are raw data files that can be opened in Excel or Google Sheets for further analysis.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
